import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LedgerEntryStatus } from '@/drivers/driver-ledger/_models/driver-ledger.model';

export interface DriverLedgerRow {
	driverId: string;
	firstName: string;
	lastName: string;
	totalPln: number;
	totalEur: number;
	lastUpdated: string | null;
	lastUpdatedBy: string | null;
}

export interface AllSummaryParams {
	page: number;
	perPage: number;
	name?: string;
	plnMin?: number;
	plnMax?: number;
	eurMin?: number;
	eurMax?: number;
	onlyWithEntries?: boolean;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
}

export interface DriverLedgerSummaryPage {
	items: DriverLedgerRow[];
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
	sumPln: number;
	sumEur: number;
}

export interface DriverLedgerSearchRow {
	id: number;
	driverId: string;
	driverFirstName: string;
	driverLastName: string;
	date: string;
	amount: number;
	currency: string;
	amountPln: number;
	amountEur: number;
	targetCurrency: string;
	categoryId: number | null;
	categoryName: string | null;
	description: string | null;
	paymentDate: string | null;
	vehicleId: string | null;
	vehicleName: string | null;
	vehicleRegistrationNumber: string | null;
	status: LedgerEntryStatus;
	hasOriginalDocument: boolean;
	isMine: boolean;
	createdAt: string | null;
	updatedAt: string | null;
	createdBy: string | null;
	updatedBy: string | null;
	lines: import('../../driver-ledger/_models/driver-ledger.model').DriverLedgerEntryLine[];
}

export interface DriverLedgerSearchPage {
	items: DriverLedgerSearchRow[];
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

interface DriverLedgerRowApiData {
	driver_id: string;
	first_name: string;
	last_name: string;
	total_pln: number;
	total_eur: number;
	last_updated: string | null;
	last_updated_by: string | null;
}

interface DriverLedgerSearchRowApiData {
	id: number;
	driver_id: string;
	driver_first_name: string;
	driver_last_name: string;
	date: string;
	amount: number;
	currency: string;
	amount_pln: number;
	amount_eur: number;
	target_currency: string;
	category_id: number | null;
	category_name: string | null;
	description: string | null;
	payment_date: string | null;
	vehicle_id: string | null;
	vehicle_name: string | null;
	vehicle_registration_number: string | null;
	status: LedgerEntryStatus;
	has_original_document: boolean;
	is_mine: boolean;
	created_at: string | null;
	updated_at: string | null;
	created_by: string | null;
	updated_by: string | null;
	lines: import('../../driver-ledger/_models/driver-ledger.model').DriverLedgerEntryLineApiData[];
}

interface PaginatedApiResponse {
	data: {
		items: DriverLedgerRowApiData[];
		total: number;
		page: number;
		per_page: number;
		total_pages: number;
		sum_pln: number;
		sum_eur: number;
	};
}

interface SearchApiResponse {
	data: {
		items: DriverLedgerSearchRowApiData[];
		total: number;
		page: number;
		per_page: number;
		total_pages: number;
	};
}

@Injectable({
	providedIn: 'root',
})
export class DriversLedgerApiService {
	private readonly httpClient = inject(HttpClient);
	private readonly apiUrl = environment.erpSymfonyUrl;

	getAllSummary(params: AllSummaryParams): Observable<DriverLedgerSummaryPage> {
		let httpParams = new HttpParams().set('page', params.page).set('perPage', params.perPage);

		if (params.name) httpParams = httpParams.set('name', params.name);
		if (params.plnMin != null) httpParams = httpParams.set('plnMin', Math.round(params.plnMin * 100));
		if (params.plnMax != null) httpParams = httpParams.set('plnMax', Math.round(params.plnMax * 100));
		if (params.eurMin != null) httpParams = httpParams.set('eurMin', Math.round(params.eurMin * 100));
		if (params.eurMax != null) httpParams = httpParams.set('eurMax', Math.round(params.eurMax * 100));
		if (params.onlyWithEntries) httpParams = httpParams.set('onlyWithEntries', true);
		if (params.sortBy) httpParams = httpParams.set('sort_by', params.sortBy);
		if (params.sortDirection) httpParams = httpParams.set('sort_direction', params.sortDirection);

		return this.httpClient
			.get<PaginatedApiResponse>(`${this.apiUrl}/driver-ledger/all-summary`, { params: httpParams })
			.pipe(
				map((response) => ({
					items: response.data.items.map((row) => ({
						driverId: row.driver_id,
						firstName: row.first_name,
						lastName: row.last_name,
						totalPln: row.total_pln,
						totalEur: row.total_eur,
						lastUpdated: row.last_updated,
						lastUpdatedBy: row.last_updated_by,
					})),
					total: response.data.total,
					page: response.data.page,
					perPage: response.data.per_page,
					totalPages: response.data.total_pages,
					sumPln: response.data.sum_pln,
					sumEur: response.data.sum_eur,
				})),
			);
	}

	searchEntries(query: string, page: number, perPage: number): Observable<DriverLedgerSearchPage> {
		const httpParams = new HttpParams().set('q', query).set('page', page).set('perPage', perPage);

		return this.httpClient
			.get<SearchApiResponse>(`${this.apiUrl}/driver-ledger/search`, { params: httpParams })
			.pipe(
				map((response) => ({
					items: response.data.items.map((row) => ({
						id: row.id,
						driverId: row.driver_id,
						driverFirstName: row.driver_first_name,
						driverLastName: row.driver_last_name,
						date: row.date,
						amount: row.amount,
						currency: row.currency,
						amountPln: row.amount_pln,
						amountEur: row.amount_eur,
						targetCurrency: row.target_currency,
						categoryId: row.category_id,
						categoryName: row.category_name,
						description: row.description,
						paymentDate: row.payment_date,
						vehicleId: row.vehicle_id,
						vehicleName: row.vehicle_name,
						vehicleRegistrationNumber: row.vehicle_registration_number,
						status: row.status,
						hasOriginalDocument: row.has_original_document,
						isMine: row.is_mine,
						createdAt: row.created_at,
						updatedAt: row.updated_at,
						createdBy: row.created_by,
						updatedBy: row.updated_by,
						lines: (row.lines ?? []).map((l) => ({
							id: l.id,
							categoryId: l.category_id,
							categoryName: l.category_name,
							amount: l.amount,
							description: l.description,
							position: l.position,
						})),
					})),
					total: response.data.total,
					page: response.data.page,
					perPage: response.data.per_page,
					totalPages: response.data.total_pages,
				})),
			);
	}
}
